import Swiper from 'swiper';

type KinescopePlayer = {
  Events: {
    Loaded: string;
    Pause: string;
    Play: string;
    Playing: string;
    Waiting: string;
  };
  destroy(): Promise<void>;
  mute(): Promise<void>;
  on(type: string, listener: () => void): void;
  pause(): Promise<void>;
  play(): Promise<void>;
  unmute(): Promise<void>;
};

// Loading an iframe and buffering an already-created player are different concerns.
// `LoadState` belongs to the one-at-a-time preload scheduler; `PlayerState` drives the controls.
type LoadState = 'idle' | 'loading' | 'ready';
type PlayerState = 'loading' | 'paused' | 'playing';
type PlayerRecord = { player: KinescopePlayer; state: PlayerState };
type SwiperWithActiveIndexUpdate = Swiper & { updateActiveIndex(): void };

function isLoadState(value: string | undefined): value is LoadState {
  return value === 'idle' || value === 'loading' || value === 'ready';
}

function canUpdateActiveIndex(swiper: Swiper): swiper is SwiperWithActiveIndexUpdate {
  return 'updateActiveIndex' in swiper && typeof swiper.updateActiveIndex === 'function';
}

type KinescopeCreateOptions = {
  url: string;
  behavior: {
    autoPause: boolean;
    autoPlay: boolean;
    localStorage: boolean;
    loop: boolean;
    maxAbrQuality: number;
    muted: boolean;
    preload: boolean;
  };
  ui: { controls: boolean; mainPlayButton: boolean };
};

type KinescopeFactory = {
  create(elementId: string, options: KinescopeCreateOptions): Promise<KinescopePlayer>;
};

declare global {
  interface Window {
    onKinescopeIframeAPIReady?: (factory: KinescopeFactory) => void;
  }
}

let kinescopeFactory: KinescopeFactory | null = null;
let apiRequested = false;
let nextPlayerId = 0;
const factoryCallbacks: Array<(factory: KinescopeFactory) => void> = [];
const muteStorageKey = 'pdr_remzona_mute';
// This module owns playback, sound persistence, and controls. Kinescope only provides media playback.
const kinescopePlayerOptions: Omit<KinescopeCreateOptions, 'url'> = {
  behavior: {
    autoPause: false,
    autoPlay: false,
    // Prevent Kinescope preferences from competing with pdr_remzona_mute.
    localStorage: false,
    loop: true,
    // These portrait cards do not benefit from downloading 1080p+ renditions.
    maxAbrQuality: 720,
    muted: true,
    preload: true,
  },
  ui: { controls: false, mainPlayButton: false },
};

function loadKinescopeApi(callback: (factory: KinescopeFactory) => void) {
  if (kinescopeFactory) {
    callback(kinescopeFactory);
    return;
  }

  // Kinescope exposes one global readiness callback, so all sliders share the script and queue here.
  factoryCallbacks.push(callback);
  if (apiRequested) return;
  apiRequested = true;

  window.onKinescopeIframeAPIReady = (factory) => {
    kinescopeFactory = factory;
    factoryCallbacks.splice(0).forEach((ready) => ready(factory));
  };

  const script = document.createElement('script');
  script.src = 'https://player.kinescope.io/latest/iframe.player.js';
  script.async = true;
  document.head.append(script);
}

function initRemzone(element: HTMLElement) {
  const players = new Map<HTMLIFrameElement, PlayerRecord>();
  const iframes = Array.from(
    element.querySelectorAll<HTMLIFrameElement>('.pdr-remzone-card iframe')
  );
  let committedIndex = 0;
  let playbackRevision = 0;
  let soundEnabled = false;
  let keepMuted = false;
  let userGesture = false;
  let isDragging = false;
  let pendingLoad: HTMLIFrameElement | null = null;
  let committedLoadPending = false;
  let lazyLoadStarted = false;
  let isPlaybackVisible = false;
  let automaticAudioChecked = false;

  // Storage may be denied in privacy modes; the safe fallback is browser-required muted playback.
  try {
    keepMuted = localStorage.getItem(muteStorageKey) === 'true';
  } catch {}

  const committedIframe = () => iframes[committedIndex] ?? null;

  const getLoadState = (iframe: HTMLIFrameElement) => {
    const state = iframe.dataset.loadState;
    return isLoadState(state) ? state : 'idle';
  };

  // Loading scheduler

  function startLoad(iframe: HTMLIFrameElement) {
    const index = iframes.indexOf(iframe);
    const source = iframe.dataset.src;
    if (
      index === -1 ||
      !source ||
      !kinescopeFactory ||
      getLoadState(iframe) !== 'idle' ||
      pendingLoad
    ) {
      return;
    }

    // A retry gets a fresh iframe and ID so Kinescope never reconnects a failed player attempt.
    const loadAttempt = iframe.cloneNode(false);
    if (!(loadAttempt instanceof HTMLIFrameElement)) return;

    loadAttempt.id = `pdr-remzone-player-${++nextPlayerId}`;
    loadAttempt.dataset.loadState = 'loading';
    iframe.replaceWith(loadAttempt);
    iframes[index] = loadAttempt;
    pendingLoad = loadAttempt;
    if (loadAttempt === committedIframe()) {
      loadAttempt.closest('.pdr-remzone-card')?.classList.add('is-loading');
    }

    void kinescopeFactory
      .create(loadAttempt.id, {
        url: source,
        ...kinescopePlayerOptions,
      })
      .then((player) => {
        // Never remove an iframe while create() is unresolved: Kinescope has no pending-create
        // cancellation handle. This guard only protects against external DOM removal.
        if (!element.contains(loadAttempt) || getLoadState(loadAttempt) !== 'loading') {
          void player
            .destroy()
            .catch(() => {})
            .then(() => {
              if (pendingLoad === loadAttempt) pendingLoad = null;
              loadCommittedSlide();
            });
          return;
        }

        const record: PlayerRecord = { player, state: 'loading' };
        players.set(loadAttempt, record);
        updateControls(loadAttempt, record);
        monitorPlayer(loadAttempt, record);
        if (loadAttempt === committedIframe()) playCommittedSlide();
      })
      .catch(() => {
        // create() has settled, so replacing its iframe is safe. Retry automatically only when
        // navigation has superseded this request; a current-card failure waits for another click.
        const loadLatest = loadAttempt !== committedIframe();
        resetLoadAttempt(loadAttempt);
        if (loadLatest) loadCommittedSlide();
      });
  }

  function resetLoadAttempt(iframe: HTMLIFrameElement) {
    const index = iframes.indexOf(iframe);
    if (index === -1 || getLoadState(iframe) !== 'loading') return;

    if (iframe === committedIframe()) committedLoadPending = false;
    const card = iframe.closest('.pdr-remzone-card');
    const replacement = iframe.cloneNode(false);
    if (!(replacement instanceof HTMLIFrameElement)) return;

    replacement.removeAttribute('id');
    replacement.removeAttribute('src');
    replacement.dataset.loadState = 'idle';
    players.delete(iframe);
    iframe.replaceWith(replacement);
    iframes[index] = replacement;
    card?.classList.remove('is-loading', 'is-playing');
    if (pendingLoad === iframe) pendingLoad = null;
  }

  function loadCommittedSlide() {
    if (!lazyLoadStarted) return;

    const iframe = committedIframe();
    committedLoadPending = !!iframe && getLoadState(iframe) !== 'ready';
    updateLoadingUi();
    if (!iframe || getLoadState(iframe) !== 'idle') return;

    // UI follows the latest committed card immediately. Provider work stays serial because
    // Kinescope has no cancellation handle until create() resolves; superseded loads finish as
    // useful speculative players, then the latest committed card gets the loader slot.
    if (pendingLoad && pendingLoad !== iframe) return;
    startLoad(iframe);
  }

  function markLoadReady(iframe: HTMLIFrameElement) {
    if (getLoadState(iframe) !== 'loading') return false;

    iframe.dataset.loadState = 'ready';
    if (iframe === committedIframe()) committedLoadPending = false;
    if (pendingLoad === iframe) pendingLoad = null;
    return true;
  }

  function preloadNextIdleSlide() {
    if (pendingLoad) return;

    const committed = committedIframe();
    if (!committed || getLoadState(committed) !== 'ready') {
      return;
    }

    // Continue in source order after the committed card, wrapping once at the end.
    const preloadOrder = [
      ...iframes.slice(committedIndex + 1),
      ...iframes.slice(0, committedIndex),
    ];
    const next = preloadOrder.find((iframe) => getLoadState(iframe) === 'idle');
    if (next) startLoad(next);
  }

  // Controls and playback

  function updateLoadingUi() {
    const committed = committedIframe();
    iframes.forEach((iframe) => {
      const record = players.get(iframe);
      const waiting = iframe === committed && (committedLoadPending || record?.state === 'loading');
      iframe.closest('.pdr-remzone-card')?.classList.toggle('is-loading', waiting);
    });
  }

  function updateControls(iframe: HTMLIFrameElement, record: PlayerRecord) {
    const card = iframe.closest<HTMLElement>('.pdr-remzone-card');
    const sound = iframe.parentElement?.querySelector<HTMLButtonElement>(
      '[data-remzone-action="sound"]'
    );
    if (!card || !sound) return;

    const playing = record.state === 'playing';
    const audible = iframe === committedIframe() && soundEnabled;
    // Speculative loading stays behind the poster with its play affordance intact.
    card.classList.toggle(
      'is-loading',
      iframe === committedIframe() && (committedLoadPending || record.state === 'loading')
    );
    card.classList.toggle('is-playing', playing);
    sound.classList.toggle('is-muted', !audible);
    sound.setAttribute('aria-label', audible ? 'Выключить звук' : 'Включить звук');
  }

  function updateAllControls() {
    updateLoadingUi();
    players.forEach((record, iframe) => updateControls(iframe, record));
  }

  async function pausePlayer(record: PlayerRecord) {
    if (record.state === 'paused') return;
    await record.player.pause();
    record.state = 'paused';
    updateAllControls();
  }

  const playbackIsCurrent = (iframe: HTMLIFrameElement, revision: number) =>
    revision === playbackRevision && isPlaybackVisible && iframe === committedIframe();

  function playCommittedSlide() {
    // Player commands are asynchronous. A newer revision invalidates work started for an old slide.
    const revision = ++playbackRevision;
    const iframe = committedIframe();

    if (!isPlaybackVisible) {
      players.forEach((record, playerIframe) => {
        // Do not interrupt useful speculative loading, but stop anything ready or actually playing.
        if (getLoadState(playerIframe) === 'ready' || record.state === 'playing') {
          void pausePlayer(record).catch(() => {});
        }
      });
      return;
    }

    void (async () => {
      // A speculative player has not played, so pausing it before Loaded only disrupts preloading.
      await Promise.all(
        Array.from(players).map(([playerIframe, record]) =>
          playerIframe === iframe || getLoadState(playerIframe) === 'loading'
            ? Promise.resolve()
            : pausePlayer(record).catch(() => {})
        )
      );

      if (!iframe || !playbackIsCurrent(iframe, revision)) return;

      const record = players.get(iframe);
      if (!record) return;

      const shouldCheckAutomaticAudio = !automaticAudioChecked && getLoadState(iframe) === 'ready';
      if (shouldCheckAutomaticAudio) automaticAudioChecked = true;
      const tryAudible = !keepMuted && (soundEnabled || shouldCheckAutomaticAudio);
      let played = false;
      if (tryAudible) {
        soundEnabled = true;
        try {
          await record.player.unmute();
          updateAllControls();
          if (!playbackIsCurrent(iframe, revision)) return;
          await record.player.play();
          played = true;
        } catch {
          // The player Promise is the only reliable autoplay permission check.
          if (!playbackIsCurrent(iframe, revision)) return;
          soundEnabled = false;
          updateAllControls();
        }
      }

      if (!played) {
        await record.player.mute();
        updateAllControls();
        if (!playbackIsCurrent(iframe, revision)) return;
        await record.player.play();
      }

      if (!playbackIsCurrent(iframe, revision)) {
        await pausePlayer(record);
        return;
      }
      record.state = 'playing';
      updateControls(iframe, record);
    })().catch(() => {});
  }

  function playFromUserGesture() {
    // Browsers require a user gesture before unmuting. An explicit mute preference always wins.
    if (!keepMuted) soundEnabled = true;
    updateAllControls();
    const iframe = committedIframe();
    const record = iframe && players.get(iframe);
    if (!record) return;

    void (soundEnabled ? record.player.unmute() : record.player.mute())
      .then(() => record.player.play())
      .catch(() => {});
  }

  function commitSlide(index: number, fromUserGesture: boolean) {
    committedIndex = index;
    loadCommittedSlide();
    updateAllControls();
    // Run this immediately to retain browser user activation for unmuting. The reconciler below
    // may issue play() again, but it is what guarantees that every other player is paused first.
    if (fromUserGesture) playFromUserGesture();
    playCommittedSlide();
  }

  function togglePlayback() {
    const iframe = committedIframe();
    const record = iframe && players.get(iframe);
    if (!record) {
      loadCommittedSlide();
      return;
    }

    if (record.state === 'playing') {
      playbackRevision += 1;
      void record.player
        .pause()
        .then(() => {
          record.state = 'paused';
          updateControls(iframe, record);
        })
        .catch(() => {});
      return;
    }

    playFromUserGesture();
  }

  function toggleSound() {
    const iframe = committedIframe();
    const record = iframe && players.get(iframe);
    if (!record) return;

    soundEnabled = !soundEnabled;
    keepMuted = !soundEnabled;
    try {
      localStorage.setItem(muteStorageKey, String(keepMuted));
    } catch {}
    updateAllControls();
    void (soundEnabled ? record.player.unmute() : record.player.mute()).catch(() => {});
  }

  // Kinescope event bridge

  function monitorPlayer(iframe: HTMLIFrameElement, record: PlayerRecord) {
    function setPlayerState(state: PlayerState, restoreCommittedPlayback = false) {
      // Detached/cancelled players may still emit events; only the current Map owner may update UI.
      if (players.get(iframe) !== record) return;

      record.state = state;
      updateControls(iframe, record);
      if (iframe !== committedIframe() || !isPlaybackVisible) {
        // Initial Waiting is useful preloading. Play/Playing or buffering after readiness must stop.
        if (state !== 'paused' && (restoreCommittedPlayback || getLoadState(iframe) === 'ready')) {
          const paused = pausePlayer(record).catch(() => {});
          if (restoreCommittedPlayback && isPlaybackVisible) void paused.then(playCommittedSlide);
        }
        return;
      }

      if (state === 'playing') {
        // Keep the poster until real playback starts. Afterwards the paused video frame is preferable.
        iframe.closest('.pdr-remzone-card')?.classList.add('has-played');
        players.forEach((other, otherIframe) => {
          if (otherIframe !== iframe && getLoadState(otherIframe) !== 'loading') {
            void pausePlayer(other).catch(() => {});
          }
        });
      }
    }

    record.player.on(record.player.Events.Waiting, () => setPlayerState('loading'));
    record.player.on(record.player.Events.Loaded, () => {
      if (players.get(iframe) !== record) return;

      const becameReady = markLoadReady(iframe);
      setPlayerState('paused');
      if (iframe === committedIframe()) {
        playCommittedSlide();
      }
      if (becameReady) {
        loadCommittedSlide();
        preloadNextIdleSlide();
      }
    });
    record.player.on(record.player.Events.Play, () => setPlayerState('loading', true));
    record.player.on(record.player.Events.Playing, () => {
      setPlayerState('playing', true);
      if (iframe === committedIframe()) preloadNextIdleSlide();
    });
    record.player.on(record.player.Events.Pause, () => setPlayerState('paused'));
  }

  // Swiper interaction

  const getSlidesOffsetAfter = () =>
    window.matchMedia('(min-width: 768px)').matches ? Math.max(0, element.clientWidth - 254) : 0;

  new Swiper(element, {
    direction: 'horizontal',
    focusableElements: 'input, select, option, textarea, video, label',
    slidesPerView: 'auto',
    spaceBetween: 20,
    speed: 350,
    centeredSlides: true,
    slidesOffsetAfter: getSlidesOffsetAfter(),
    breakpoints: {
      768: {
        centeredSlides: false,
      },
    },
    on: {
      init(swiper: Swiper) {
        committedIndex = swiper.activeIndex;
        swiper.update();
      },
      resize(swiper: Swiper) {
        swiper.params.slidesOffsetAfter = getSlidesOffsetAfter();
        swiper.update();
      },
      touchStart() {
        isDragging = true;
        userGesture = true;
      },
      touchEnd(swiper: Swiper) {
        isDragging = false;
        // Swiper finalizes activeIndex after touchEnd. Commit playback in the following microtask,
        // so dragging previews card size/classes but never starts intermediate videos.
        window.queueMicrotask(() => {
          if (committedIndex !== swiper.activeIndex) commitSlide(swiper.activeIndex, true);
          userGesture = false;
        });
      },
      slideChange(swiper: Swiper) {
        if (!isDragging) commitSlide(swiper.activeIndex, userGesture);
      },
      setTranslate(swiper: Swiper) {
        if (!isDragging) return;
        // Visual enlargement follows the nearest slide during drag; playback still follows committedIndex.
        // Swiper 10 uses this runtime method internally but omits it from its public declaration.
        if (canUpdateActiveIndex(swiper)) swiper.updateActiveIndex();
        swiper.updateSlidesClasses();
      },
      click(swiper: Swiper, event: MouseEvent | TouchEvent | PointerEvent) {
        // Selection owns an inactive-card click; play/pause actions apply only to the committed card.
        if (swiper.clickedIndex !== undefined && swiper.clickedIndex !== committedIndex) {
          swiper.slideTo(swiper.clickedIndex);
          return;
        }

        const action =
          event.target instanceof Element
            ? event.target.closest<HTMLElement>('[data-remzone-action]')?.dataset.remzoneAction
            : null;
        if (action === 'play') {
          togglePlayback();
          return;
        }
        if (action === 'sound') {
          toggleSound();
          return;
        }
        togglePlayback();
      },
    },
  });

  function startLazyLoading() {
    if (lazyLoadStarted) return;

    lazyLoadStarted = true;
    loadKinescopeApi(() => {
      loadCommittedSlide();
      playCommittedSlide();
    });
  }

  function setPlaybackVisibility(visible: boolean) {
    if (isPlaybackVisible === visible) return;

    isPlaybackVisible = visible;
    if (!visible) {
      playCommittedSlide();
      return;
    }

    startLazyLoading();
    loadCommittedSlide();
    playCommittedSlide();
  }

  if ('IntersectionObserver' in window) {
    const loadObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        startLazyLoading();
      },
      { rootMargin: '500px 0px' }
    );
    loadObserver.observe(element);

    const playbackObserver = new IntersectionObserver(([entry]) => {
      setPlaybackVisibility(entry.isIntersecting);
    });
    playbackObserver.observe(element);
  } else {
    // IntersectionObserver is available in supported browsers; this keeps older ones functional.
    startLazyLoading();
    setPlaybackVisibility(true);
  }
}

export function remzone() {
  document.querySelectorAll<HTMLElement>('.js-pdr-remzone').forEach(initRemzone);
}
